import HostTopNav from './HostTopNav';
import { useRef, useEffect, useState, useMemo } from 'react';
import HostEventGalleryEditable from './HostEventGalleryEditable';
import ReviewEditButton from './ReviewEditButton';
import SubmissionStatusBanner from './SubmissionStatusBanner';
import { LuCheck, LuClock, LuCalendarDays, LuChevronDown, LuChevronLeft, LuChevronRight } from "react-icons/lu";


// ✅ PASTE THIS COMPONENT RIGHT HERE (before HostBasicInfo)
function TimeSelectDropdown({ ui, value, onChange, reviewLocked, lockedFieldStyle }) {
  const isDesktop =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine)').matches;

  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const [hh12, setHh12] = useState('');
  const [mm, setMm] = useState('');
  const [ampm, setAmpm] = useState('AM');

    function parseTo24(value) {
    if (!value) return null;

    const t = String(value).trim();

    // already 24h "HH:MM"
    if (/^\d{2}:\d{2}$/.test(t)) {
      return t;
    }

    // handle "h:mm AM/PM"
    const m = t.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (!m) return null;

    let h = Number(m[1]);
    const mm = m[2];
    const ampm = m[3].toUpperCase();

    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;

    return `${String(h).padStart(2, '0')}:${mm}`;
  }
  // close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);


    useEffect(() => {
    if (reviewLocked) {
      setOpen(false);
    }
  }, [reviewLocked]);

  // parse incoming value ("HH:MM" 24h)
useEffect(() => {
  const v24 = parseTo24(value);

  if (!v24) {
    setHh12('');
    setMm('');
    setAmpm('AM');
    return;
  }

  const [H, M] = v24.split(':').map(Number);

  const nextAmpm = H >= 12 ? 'PM' : 'AM';
  let h = H % 12;
  if (h === 0) h = 12;

  setHh12(String(h).padStart(2, '0'));
  setMm(String(M).padStart(2, '0'));
  setAmpm(nextAmpm);
}, [value]);


  function commit(nextH12 = hh12, nextM = mm, nextA = ampm) {
    if (!nextH12 || nextM === '') return;

    const h12 = Number(nextH12); // "01" -> 1
    const m = Number(nextM);
    if (!Number.isFinite(h12) || h12 < 1 || h12 > 12) return;
    if (!Number.isFinite(m) || m < 0 || m > 59) return;

    let h24 = h12 % 12;
    if (nextA === 'PM') h24 += 12;

    onChange(`${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }

  const hourOptions = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, '0')
  );
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, '0')
  );


  const displayValue =
    hh12 && mm ? `${hh12}:${mm} ${ampm}` : '';


if (isDesktop) {
  return (
    <input
      type="time"
      disabled={reviewLocked}
      value={value || ''}
      onChange={(e) => {
        if (reviewLocked) return;
        onChange(e.target.value);
      }}
      style={{
        ...ui.input,
        ...lockedFieldStyle,
        cursor: reviewLocked ? 'default' : 'pointer',
      }}
    />
  );
}

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      
      {/* Trigger (matches your CustomSelect vibe) */}
      <button
        type="button"
        disabled={reviewLocked}
        onClick={() => {
          if (reviewLocked) return;
          setOpen((v) => !v);
        }}
        style={{
          ...ui.input,
          ...lockedFieldStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          backgroundColor: reviewLocked ? '#eef0f2' : '#fff',
          cursor: reviewLocked ? 'default' : 'pointer',
          textAlign: 'left',
          paddingRight: 12, // keep clean; icon is inside
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <LuClock size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />

          <span
           style={{
  fontFamily: 'inherit',
  fontSize: 12,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
  letterSpacing: .5,   // 👈 subtle but noticeable
  color: displayValue ? '#0b0b0b' : '#9ca3af',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}}

          >
            {displayValue || 'Select time'}
          </span>
        </div>
<LuChevronDown
  aria-hidden
  style={{ fontSize: 16, color: '#9ca3af', lineHeight: 0, flexShrink: 0 }}
/>

      </button>

      {/* Dropdown panel */}
      {open && !reviewLocked && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 80,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 26px rgba(0,0,0,0.14)',
            border: '1px solid #eef0f2',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr', // HH | MM | AM/PM
              gap: 0,
              padding: 10,
            }}
          >
            {/* Column helper */}
            {(() => {
              const colStyle = {
  maxHeight: 220,
  overflowY: 'auto',
  padding: '6px 0',          // ✅ no left/right padding
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

const itemStyle = (active) => ({
  width: '78%',              // ✅ less highlight left/right (not full column)
  maxWidth: 64,              // ✅ keeps it tight + consistent
  boxSizing: 'border-box',
  border: 'none',
  background: active ? '#f3f4f6' : 'transparent',
  borderRadius: 10,
  padding: '8px 0',          // ✅ slightly tighter vertically too
  textAlign: 'center',
  fontFamily: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: active ? 900 : 600,
  letterSpacing: 0.5,
  fontSize: 12,
  color: '#0b0b0b',
  cursor: 'pointer',
  margin: '0 auto',          // ✅ centers the pill in the column
});



              return (
                <>
                  {/* HH */}
                  <div style={colStyle}>
                    {hourOptions.map((h) => (
                      <button
                        key={h}
                        type="button"
                        style={itemStyle(h === hh12)}
                        onClick={() => {
                          setHh12(h);
                          commit(h, mm, ampm);
                          // don’t auto-close yet; user may still pick minute
                        }}
                      >
                        {h}
                      </button>
                    ))}
                  </div>

                  {/* MM */}
                  <div style={colStyle}>
                    {minuteOptions.map((m) => (
                      <button
                        key={m}
                        type="button"
                        style={itemStyle(m === mm)}
                        onClick={() => {
                          setMm(m);
                          commit(hh12, m, ampm);
                          // close once minutes selected (feels natural)
                          setOpen(false);
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  {/* AM/PM */}
                  <div style={colStyle}>
                    {['AM', 'PM'].map((a) => (
                      <button
                        key={a}
                        type="button"
                        style={itemStyle(a === ampm)}
                        onClick={() => {
                          setAmpm(a);
                          commit(hh12, mm, a);
                          // keep open; they may pick minute next
                        }}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

function DateSelectDropdown({ ui, value, onChange, reviewLocked, lockedFieldStyle }) {
  const isDesktop =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine)').matches;

  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  // value is "YYYY-MM-DD" (same as your current eventDate)
  const [viewYear, setViewYear] = useState(() => {
    const d = value ? new Date(value + 'T00:00:00') : new Date();
    return d.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? new Date(value + 'T00:00:00') : new Date();
    return d.getMonth();
  });


  // close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // if locked, force close
  useEffect(() => {
    if (reviewLocked) {
      setOpen(false);
    }
  }, [reviewLocked]);

  // keep view synced when value changes externally
  useEffect(() => {
    if (!value) return;
    const d = new Date(value + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return;
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [value]);


  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const buildCalendarGrid = (year, monthIndex) => {
    const first = new Date(year, monthIndex, 1);
    const last = new Date(year, monthIndex + 1, 0);
    const firstWeekday = first.getDay();
    const lastWeekday = last.getDay();

    const days = [];
    // leading
    for (let i = 0; i < firstWeekday; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() - (firstWeekday - i));
      days.push(d);
    }
    // month
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, monthIndex, d));
    // trailing
    for (let i = lastWeekday; i < 6; i++) {
      const d = new Date(last);
      d.setDate(d.getDate() + (i - lastWeekday + 1));
      days.push(d);
    }
    return days;
  };

  const selectedDate = useMemo(() => {
    if (!value) return null;
    const d = new Date(value + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? null : d;
  }, [value]);

  const monthDate = useMemo(() => new Date(viewYear, viewMonth, 1), [viewYear, viewMonth]);
  const days = useMemo(() => buildCalendarGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const monthLabel = monthDate.toLocaleString('default', { month: 'long' });

  const fmtLabel = (d) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const displayValue = selectedDate ? fmtLabel(selectedDate) : '';

  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const goPrevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goNextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  
  const headerBtn = {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    width: 32,
    height: 32,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: 0,
    lineHeight: 0,
    boxSizing: 'border-box',
  };

  

  const DayCell = ({ day }) => {
    const inMonth = day.getMonth() === viewMonth && day.getFullYear() === viewYear;
    const isSelected = selectedDate && isSameDay(day, selectedDate);

    const base = {
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 10,
      fontSize: 13,
      cursor: 'pointer',
      position: 'relative',
      userSelect: 'none',
      border: '1px solid transparent',
      background: 'transparent',
      color: inMonth ? '#0b0b0b' : '#9ca3af',
    };

    if (isSelected) {
      base.background = '#0b0b0b';
      base.border = '1px solid #0b0b0b';
      base.color = '#fff';
    }

    return (
      <button
        type="button"
        aria-label={day.toDateString()}
        style={base}
        onClick={() => {
          const picked = startOfDay(day);
          onChange(toYMD(picked));
          setOpen(false);
        }}
      >
        <span style={{ fontWeight: isSelected ? 900 : 700 }}>{day.getDate()}</span>
      </button>
    );
  };

  if (isDesktop) {
  return (
    <input
      type="date"
      disabled={reviewLocked}
      value={value || ''}
      onChange={(e) => {
        if (reviewLocked) return;
        onChange(e.target.value);
      }}
      style={{
        ...ui.input,
        ...lockedFieldStyle,
        cursor: reviewLocked ? 'default' : 'pointer',
      }}
    />
  );
}

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger (matches your CustomSelect vibe) */}
      <button
        type="button"
        disabled={reviewLocked}
        onClick={() => {
          if (reviewLocked) return;
          setOpen((v) => !v);
        }}
        style={{
          ...ui.input,
          ...lockedFieldStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          backgroundColor: reviewLocked ? '#eef0f2' : '#fff',
          cursor: reviewLocked ? 'default' : 'pointer',
          textAlign: 'left',
          paddingRight: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <LuCalendarDays style={{ fontSize: 18, color: '#9ca3af', flexShrink: 0 }} />
         <span
  style={{
    fontFamily: 'inherit',
     fontSize: 12,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
  letterSpacing: .5,   // 👈 subtle but noticeable
    letterSpacing: 1.25,
    color: displayValue ? '#0b0b0b' : '#9ca3af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }}
>
  {displayValue || 'Select date'}
</span>

        </div>
<LuChevronDown
  aria-hidden
  style={{ fontSize: 16, color: '#9ca3af', lineHeight: 0, flexShrink: 0 }}
/>

      </button>

      {open && !reviewLocked && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 80,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 26px rgba(0,0,0,0.14)',
            border: '1px solid #eef0f2',
            overflow: 'hidden',
          }}
        >
          {/* Calendar card */}
          <div style={{ padding: '10px 12px 12px' }}>
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                paddingBottom: 10,
                borderBottom: '1px solid #eef0f2',
              }}
            >
              <button type="button" onClick={goPrevMonth} style={headerBtn} aria-label="Previous month">
                <LuChevronLeft style={{ width: 12, height: 12, display: 'block', color: '#111827' }} />
              </button>

              <div style={{ fontWeight: 900, fontSize: 14, color: '#0b0b0b' }}>
  {monthLabel} {viewYear}
</div>


              <button type="button" onClick={goNextMonth} style={headerBtn} aria-label="Next month">
                <LuChevronRight style={{ width: 12, height: 12, display: 'block', color: '#111827' }} />
              </button>
            </div>

            {/* Weekdays */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 8,
                color: '#6b7280',
                fontSize: 12,
                fontWeight: 800,
                padding: '10px 2px 8px',
              }}
            >
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
                <div key={d} style={{ textAlign: 'center' }}>{d}</div>
              ))}
            </div>

            {/* Days */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              {days.map((d) => (
                <DayCell key={d.toISOString()} day={d} />
              ))}
            </div>

            {/* Footer helper row */}
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
           
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                style={{
                  border: '1px solid #e5e7eb',
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: '8px 10px',
                  fontSize: 12,
                  fontWeight: 800,
                  color: '#0b0b0b',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function HostBasicInfo({
  ui,
  reviewLocked,
 inModal = false,
   isReviewFlow,
  onEdit,
  onCancel,


    submissionStatus,
  adminNote,

  PURPOSE_OPTIONS,
  TAG_OPTIONS,
  VENUE_TYPES,
  DRESS_CODES,

  eventName,
  setEventName,
  twist,
  setTwist,
  eventDate,
  setEventDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,

  address,
  setAddress,
  locationCoords, // kept for architecture parity (not used in JSX currently)
  setLocationCoords,
  addressQuery,
  setAddressQuery,
  addressPredictions,
  setAddressPredictions,
  addressEditing,
  setAddressEditing,
  size,
  setSize,
  hasEditedAddress,
  setHasEditedAddress,
  addressInputRef,

  minAge,
  setMinAge,
  maxAge,
  setMaxAge,

  genderGate,
  setGenderGate,
  sexualOrientation,
  setSexualOrientation,
  affinityText,
  setAffinityText,

  purposeTag,
  setPurposeTag,
  genderSplit,
  setGenderSplit,

  // ✅ NEW: lane cap counts (must sum to total Size)
  maleCapCount,
  setMaleCapCount,
  femaleCapCount,
  setFemaleCapCount,

  eventTags,
  toggleTag,
  selectedTagLabels,

  venueType,
  setVenueType,
  dressCode,
  setDressCode,

  description,
  setDescription,

  galleryImages,
  setGalleryImages,

  handleAddressChange,
}) {
  // ------------------------------------------------------------------
  // Review-locked styling (grey fill, NOT faded)
  // ------------------------------------------------------------------
  const lockedFieldStyle = reviewLocked
    ? {
        backgroundColor: '#eef0f2',
        borderColor: '#e5e7eb',
        cursor: 'default',
        opacity: 1,
        color: '#0b0b0b',
        WebkitTextFillColor: '#0b0b0b',
      }
    : {};

  const lockedPillStyle = reviewLocked
    ? {
        background: '#eef0f2',
        cursor: 'default',
        opacity: 1,
      }
    : {};

    const requiredAsterisk = {
  color: '#dc2626', // red-600
  marginLeft: 4,
  fontWeight: 900,
};

function RequiredIndicator({ complete }) {
  if (complete) {
    return (
   <LuCheck
  size={14}
  style={{
    marginLeft: 6,
    color: '#16a34a', // green-600
    strokeWidth: 3,

    position: 'relative',
    top: -1,          // 👈 move up 4px

    verticalAlign: 'middle',
  }}
/>

    );
  }

  return (
    <span
      style={{
        color: '#dc2626', // red-600
        marginLeft: 4,
        fontWeight: 900,
      }}
    >
      *
    </span>
  );
}

  // ------------------------------------------------------------------
  // Backward-safe fallbacks (won't crash if parent hasn't passed these yet)
  // ------------------------------------------------------------------
  const [localMaleCapCount, setLocalMaleCapCount] = useState('');
  const [localFemaleCapCount, setLocalFemaleCapCount] = useState('');

  const menCapVal =
    typeof maleCapCount === 'undefined' ? localMaleCapCount : maleCapCount;
  const womenCapVal =
    typeof femaleCapCount === 'undefined' ? localFemaleCapCount : femaleCapCount;

  const setMenCap =
    typeof setMaleCapCount === 'function' ? setMaleCapCount : setLocalMaleCapCount;
  const setWomenCap =
    typeof setFemaleCapCount === 'function'
      ? setFemaleCapCount
      : setLocalFemaleCapCount;


      function coerceAgePair(nextMin, nextMax) {
  // Both empty → allowed
  if (nextMin === '' && nextMax === '') {
    return { min: '', max: '' };
  }

  // Min set, max missing → force max = min
  if (nextMin !== '' && nextMax === '') {
    return { min: nextMin, max: nextMin };
  }

  // Max set, min missing → force min = max
  if (nextMax !== '' && nextMin === '') {
    return { min: nextMax, max: nextMax };
  }

  // Both set → ensure min ≤ max
  const minNum = Number(nextMin);
  const maxNum = Number(nextMax);

  if (!Number.isNaN(minNum) && !Number.isNaN(maxNum) && minNum > maxNum) {
    return { min: nextMax, max: nextMin };
  }

  return { min: nextMin, max: nextMax };
}


  function CustomSelect({ ui, value, onChange, placeholder, options }) {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      const onClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
  setOpen(false);
}
      };
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }, []);

  useEffect(() => {
    if (reviewLocked) {
      setOpen(false);
    }
  }, [reviewLocked]);
  
    const selectedLabel = options.find((o) => o.value === value)?.label || '';

    return (
      
      <div ref={ref} style={{ position: 'relative' }}>
      
        {/* Input-looking trigger */}
        <button
          type="button"
          disabled={reviewLocked}
          onClick={() => {
            if (reviewLocked) return;
            setOpen((v) => !v);
          }}
          style={{
            ...ui.input,
            ...lockedFieldStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
            backgroundColor: reviewLocked ? '#eef0f2' : '#fff',
            cursor: reviewLocked ? 'default' : 'pointer',
            textAlign: 'left',
          }}
        >
<span
  style={{
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 450,
    letterSpacing: 0.6,
    color: selectedLabel ? '#0b0b0b' : '#9ca3af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }}
>
  {selectedLabel || placeholder}
</span>

<LuChevronDown
  aria-hidden
  style={{ fontSize: 16, color: '#9ca3af', lineHeight: 0, flexShrink: 0 }}
/>


        </button>

        {/* Dropdown (matches address autocomplete) */}
        {open && !reviewLocked && (
          <ul
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 50,
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              listStyle: 'none',
              padding: 0,
              margin: '4px 0 0',
              maxHeight: 240,
              overflowY: 'auto',
            }}
          >
            {options.map((opt) => (
              <li
                key={opt.value}
                onClick={() => {
                  if (reviewLocked) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
              style={{
  padding: '8px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: opt.value === value ? 650 : 550,
  letterSpacing: 0.25,
  color: '#0b0b0b',
}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ==================================================================
  // Neutral "basic logic" for Gender Gate + Sexual Orientation + Lanes
  // ==================================================================

  const capNum = size === '' || size == null ? 0 : Number(size) || 0;

  function normalizeOrientation(v) {
    return String(v || 'all').toLowerCase();
  }

  function coerceGenderGateForOrientation(gate, orientation) {
    const g = String(gate || 'all').toLowerCase();
    const o = normalizeOrientation(orientation);

    // If orientation implies a single gender, force the corresponding gate
    if (o === 'gay_men') return 'men_only';
    if (o === 'gay_women') return 'women_only';
    return g;
  }

  function coerceOrientationForGenderGate(gate, orientation) {
    const g = String(gate || 'all').toLowerCase();
    const o = normalizeOrientation(orientation);

    // If gate is men_only, can't be gay_women
    if (g === 'men_only' && o === 'gay_women') return 'all';

    // If gate is women_only, can't be gay_men
    if (g === 'women_only' && o === 'gay_men') return 'all';

    return o;
  }

  function applyGateAndOrientation(nextGate, nextOrientation) {
    const gate1 = String(nextGate || genderGate || 'all').toLowerCase();
    const ori1 = normalizeOrientation(nextOrientation ?? sexualOrientation ?? 'all');

    // Orientation can force gate (gay men/women)
    const gate2 = coerceGenderGateForOrientation(gate1, ori1);

    // Gate can invalidate orientation
    const ori2 = coerceOrientationForGenderGate(gate2, ori1);

    if (gate2 !== String(genderGate || 'all').toLowerCase()) setGenderGate(gate2);
    if (ori2 !== normalizeOrientation(sexualOrientation || 'all'))
      setSexualOrientation(ori2);
  }

  // Keep current values valid if either changes
  useEffect(() => {
    applyGateAndOrientation(genderGate, sexualOrientation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genderGate, sexualOrientation]);

  const orientationKey = normalizeOrientation(sexualOrientation || 'all');
  const gateKey = String(genderGate || 'all').toLowerCase();

  const isGaySingleGender =
    orientationKey === 'gay_men' || orientationKey === 'gay_women';

  const isSingleGenderGate = gateKey === 'men_only' || gateKey === 'women_only';

  // Lane caps allowed only when:
  // - size is set
  // - gate is not men_only/women_only
  // - orientation is not gay_men/gay_women
  const laneCapsHardBlocked = capNum <= 0 || isSingleGenderGate || isGaySingleGender;

  const laneCapsOn = String(genderSplit || 'no') === 'yes';
  const laneCapsEnabled = laneCapsOn && !laneCapsHardBlocked;

  // Track whether we auto-filled caps (so size changes can re-auto-split)
  const laneCapsAutoRef = useRef(false);

  // If we become hard-blocked, force lane caps OFF + clear counts
  useEffect(() => {
    if (!laneCapsHardBlocked) return;

    if (laneCapsOn) setGenderSplit('no');

    const mRaw = String(menCapVal ?? '').trim();
    const fRaw = String(womenCapVal ?? '').trim();
    if (mRaw !== '' || fRaw !== '') {
      setMenCap('');
      setWomenCap('');
    }

    laneCapsAutoRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneCapsHardBlocked]);

  // When lane caps turn ON, default to 50/50
  useEffect(() => {
    if (!laneCapsEnabled) return;

    const mRaw = String(menCapVal ?? '').trim();
    const fRaw = String(womenCapVal ?? '').trim();
    const bothBlank = mRaw === '' && fRaw === '';

    // Only auto-fill if blank OR previously auto-filled
    if (!bothBlank && !laneCapsAutoRef.current) return;

    const cap = capNum;
    if (cap <= 0) return;

    const men = Math.floor(cap / 2);
    const women = cap - men;

    setMenCap(String(men));
    setWomenCap(String(women));
    laneCapsAutoRef.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneCapsEnabled]);

  // If size changes and the caps were auto-filled, re-split 50/50
  useEffect(() => {
    if (!laneCapsEnabled) return;
    if (!laneCapsAutoRef.current) return;

    const cap = capNum;
    if (cap <= 0) return;

    const men = Math.floor(cap / 2);
    const women = cap - men;

    setMenCap(String(men));
    setWomenCap(String(women));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capNum]);

  const menNum = Number(menCapVal || 0);
  const womenNum = Number(womenCapVal || 0);

  const laneCapsValid = !laneCapsEnabled || menNum + womenNum === capNum;

  // If reviewLocked turns on, close address autocomplete UI
  useEffect(() => {
    if (!reviewLocked) return;
    if (addressEditing) setAddressEditing(false);
    if (addressPredictions?.length) setAddressPredictions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewLocked]);


 


  return (
    <>
<HostTopNav
  title={isReviewFlow ? 'Update Event' : 'Add Event'}
  step={1}
  totalSteps={2}
  backTo="/host"
 hideChrome={inModal}

/>

      {/* ========================= Basic Info ========================== */}
      <div style={{ padding: '0 16px' }}>
        <div style={{ ...ui.block, borderTop: 'none', marginTop: 0 }}>
      
 {isReviewFlow && (
  <SubmissionStatusBanner
    status={submissionStatus}
    adminNote={adminNote}
  />
)}
          {/* Section title */}
      <div style={ui.blockHeader}>
  <div style={ui.sectionLabel}>Basic Info</div>

{submissionStatus !== 'rejected' && (
  <ReviewEditButton
    isReviewFlow={isReviewFlow}
    reviewLocked={reviewLocked}
    onEdit={onEdit}
    onCancel={onCancel}
  />
)}

</div>

{/* Image gallery (field-level label) */}
<div style={{ marginBottom: 6 }}>
<div style={ui.fieldLabel}>
  Image gallery{' '}
  <RequiredIndicator
    complete={Array.isArray(galleryImages) && galleryImages.length === 3}
  />
</div>

</div>

<HostEventGalleryEditable
  images={galleryImages}
  maxImages={3}
  readOnly={reviewLocked}
    inModal={inModal} 
  onAddImages={(nextImages) => {
    if (reviewLocked) return;
    setGalleryImages(nextImages);
  }}
/>


          {/* Event name */}
          <div>
<div style={ui.fieldLabel}>
  Event name <RequiredIndicator complete={!!eventName} />
</div>

            <input
              disabled={reviewLocked}
              style={{ ...ui.input, ...lockedFieldStyle, paddingLeft: 12 }}
              value={eventName}
              onChange={(e) => {
                if (reviewLocked) return;
                setEventName(e.target.value);
              }}
              placeholder="Sunset Hike + Picnic in the Park"
            />
          </div>

          {/* Date */}
          <div>
<div style={ui.fieldLabel}>
  Date <RequiredIndicator complete={!!eventDate} />
</div>

       <DateSelectDropdown
  ui={ui}
  value={eventDate}
  reviewLocked={reviewLocked}
  lockedFieldStyle={lockedFieldStyle}
  onChange={(v) => {
    if (reviewLocked) return;
    setEventDate(v);
  }}
/>

          </div>

          {/* Start + End time */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gap: 10,
            }}
          >
            <div>
<div style={ui.fieldLabel}>
  Start time <RequiredIndicator complete={!!startTime} />
</div>

            <TimeSelectDropdown
  ui={ui}
  value={startTime}
  reviewLocked={reviewLocked}
  lockedFieldStyle={lockedFieldStyle}
  onChange={(v) => {
    if (reviewLocked) return;
    setStartTime(v);
    if (endTime && v >= endTime) setEndTime(v);
  }}
/>

            </div>

            <div
              style={{
                width: 12,
                height: 2,
                backgroundColor: '#d1d5db',
                marginTop: 40,
              }}
            />

            <div>
<div style={ui.fieldLabel}>
  End time <RequiredIndicator complete={!!endTime} />
</div>

            <TimeSelectDropdown
  ui={ui}
  value={endTime}
  reviewLocked={reviewLocked}
  lockedFieldStyle={lockedFieldStyle}
  onChange={(v) => {
    if (reviewLocked) return;
    setEndTime(v);
    if (startTime && v <= startTime) setStartTime(v);
  }}
/>

            </div>
          </div>

          {/* Address (own line) */}
          <div style={{ position: 'relative' }}>
<div style={ui.fieldLabel}>
  Address <RequiredIndicator complete={!!address} />
</div>

            <input
              ref={addressInputRef}
              disabled={reviewLocked}
              style={{ ...ui.input, ...lockedFieldStyle }}
              value={addressEditing ? addressQuery : address}
              placeholder="123 Main St Apt 4B, Anytown, CA 90210, United States"
              onFocus={(e) => {
                if (reviewLocked) return;

                setAddressEditing(true);
                setHasEditedAddress(false);

                // keep full address, put cursor at the end
                const val = address || '';
                setAddressQuery(val);

                requestAnimationFrame(() => {
                  const el = e.target;
                  const len = el.value.length;
                  el.setSelectionRange(len, len);
                });
              }}
              onKeyDown={(e) => {
                if (reviewLocked) return;

                // First delete or character clears everything
                if (
                  !hasEditedAddress &&
                  (e.key === 'Backspace' || e.key.length === 1)
                ) {
                  e.preventDefault();
                  setHasEditedAddress(true);
                  setAddressQuery('');
                  setAddress('');
                  setAddressPredictions([]);
                }
              }}
              onChange={(e) => {
                if (reviewLocked) return;

                const val = e.target.value;

                if (!hasEditedAddress) {
                  setHasEditedAddress(true);
                  setAddressQuery('');
                  setAddress('');
                }

                setAddressQuery(val);
                handleAddressChange(e);
              }}
            />

            {!reviewLocked && addressEditing && addressPredictions.length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 50,
                  background: '#fff',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  listStyle: 'none',
                  padding: 0,
                  margin: '4px 0 0',
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {addressPredictions.map((p, idx) => (
                  <li
                    key={idx}
                    onClick={() => {
                      if (reviewLocked) return;

                      setAddress(p.formattedAddress);
                      setAddressQuery('');
                      setLocationCoords({
                        lat: p.latitude,
                        lon: p.longitude,
                      });
                      setAddressEditing(false);
                      setAddressPredictions([]);
                      setHasEditedAddress(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {p.formattedAddress}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Size */}
          <div>
<div style={ui.fieldLabel}>
  Size <RequiredIndicator complete={!!size} />
</div>


            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 8,
                alignItems: 'stretch',
              }}
            >
              {/* Size input */}
              <input
                type="number"
                disabled={reviewLocked}
                style={{ ...ui.input, ...lockedFieldStyle }}
                value={size}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setSize(e.target.value);
                }}
                onBlur={() => {
                  if (reviewLocked) return;
                  if (size === '') return;

                  let n = Number(size);
                  if (Number.isNaN(n)) {
                    setSize('');
                    return;
                  }

                  n = Math.max(25, Math.min(250, n));
                  setSize(String(n));
                }}
                placeholder="25"
              />

              {/* +/- controls */}
              <div style={{ display: 'flex', gap: 6 }}>
                {/* minus */}
                <button
                  type="button"
                  disabled={reviewLocked || Number(size || 25) <= 25}
                  onClick={() => {
                    if (reviewLocked) return;

                    let n = size === '' ? 25 : Number(size);
                    if (Number.isNaN(n)) n = 25;
                    n = Math.max(25, n - 1);
                    setSize(String(n));
                  }}
                  style={{
                    ...ui.sizeBtn,
                    ...(reviewLocked || Number(size || 25) <= 25 ? ui.sizeBtnDisabled : {}),
                    ...(reviewLocked ? { background: '#eef0f2', cursor: 'default' } : {}),
                    opacity: 1,
                  }}
                >
                  −
                </button>

                {/* plus */}
                <button
                  type="button"
                  disabled={reviewLocked || Number(size || 25) >= 250}
                  onClick={() => {
                    if (reviewLocked) return;

                    let n = size === '' ? 25 : Number(size);
                    if (Number.isNaN(n)) n = 25;
                    n = Math.min(250, n + 1);
                    setSize(String(n));
                  }}
                  style={{
                    ...ui.sizeBtn,
                    ...(reviewLocked || Number(size || 25) >= 250 ? ui.sizeBtnDisabled : {}),
                    ...(reviewLocked ? { background: '#eef0f2', cursor: 'default' } : {}),
                    opacity: 1,
                  }}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Filters row */}
          <div style={{ marginTop: 8 }}>
            <div style={ui.grid2}>
              <div>
<div style={ui.fieldLabel}>
  Gender <RequiredIndicator complete={!!genderGate} />
</div>

                <CustomSelect
                  ui={ui}
                  value={genderGate}
                  onChange={(v) => applyGateAndOrientation(v, sexualOrientation)}
                  placeholder="Gender"
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'women_only', label: 'Women only' },
                    { value: 'men_only', label: 'Men only' },
                  ]}
                />
              </div>

              <div>
<div style={ui.fieldLabel}>
  Sexual orientation <RequiredIndicator complete={!!sexualOrientation} />
</div>

                <CustomSelect
                  ui={ui}
                  value={sexualOrientation}
                  onChange={(v) => applyGateAndOrientation(genderGate, v)}
                  placeholder="Sexual orientation"
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'heterosexual', label: 'Heterosexual' },
                    { value: 'gay_men', label: 'Gay men' },
                    { value: 'gay_women', label: 'Gay women' },
                  ]}
                />
              </div>
            </div>

            <div style={{ marginTop: 4 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                <div style={ui.fieldLabel}>Age range (optional)</div>

                {(minAge !== '' || maxAge !== '') && (
                  <button
                    type="button"
                    disabled={reviewLocked}
                    onClick={() => {
                      if (reviewLocked) return;
                      setMinAge('');
                      setMaxAge('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontSize: 12,
                      fontWeight: 700,
                      color: reviewLocked ? '#9ca3af' : '#2563eb',
                      cursor: reviewLocked ? 'default' : 'pointer',
                      opacity: 1,
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <input
                  type="number"
                  disabled={reviewLocked}
                  style={{ ...ui.input, ...lockedFieldStyle }}
                  value={minAge}
                  onChange={(e) => {
                    if (reviewLocked) return;
                    setMinAge(e.target.value);
                  }}
                  onBlur={() => {
  if (reviewLocked) return;

  const minVal = minAge === '' ? '' : String(Math.max(18, Math.min(100, Number(minAge) || 0)));
  const maxVal = maxAge === '' ? '' : String(Math.max(18, Math.min(100, Number(maxAge) || 0)));

  // If both empty → allowed
  if (minVal === '' && maxVal === '') {
    setMinAge('');
    setMaxAge('');
    return;
  }

  // If min set but max missing → force max = min
  if (minVal !== '' && maxVal === '') {
    setMinAge(minVal);
    setMaxAge(minVal);
    return;
  }

  // If max set but min missing → force min = max
  if (maxVal !== '' && minVal === '') {
    setMinAge(maxVal);
    setMaxAge(maxVal);
    return;
  }

  // Both set → ensure min ≤ max
  if (Number(minVal) > Number(maxVal)) {
    setMinAge(maxVal);
    setMaxAge(minVal);
    return;
  }

  setMinAge(minVal);
  setMaxAge(maxVal);
}}
placeholder="18+"
                />

                {/* solid divider */}
                <div
                  style={{
                    width: 12,
                    height: 2,
                    backgroundColor: '#d1d5db',
                    marginBottom: 10,
                  }}
                />

                <input
                  type="number"
                  disabled={reviewLocked}
                  style={{ ...ui.input, ...lockedFieldStyle }}
                  value={maxAge}
                  onChange={(e) => {
                    if (reviewLocked) return;
                    setMaxAge(e.target.value);
                  }}
               onBlur={() => {
  if (reviewLocked) return;

  const minVal =
    minAge === '' ? '' : String(Math.max(18, Math.min(100, Number(minAge) || 0)));
  const maxVal =
    maxAge === '' ? '' : String(Math.max(18, Math.min(100, Number(maxAge) || 0)));

  // Both empty → allowed
  if (minVal === '' && maxVal === '') {
    setMinAge('');
    setMaxAge('');
    return;
  }

  // Max set but min missing → force min = max
  if (maxVal !== '' && minVal === '') {
    setMinAge(maxVal);
    setMaxAge(maxVal);
    return;
  }

  // Min set but max missing → force max = min
  if (minVal !== '' && maxVal === '') {
    setMinAge(minVal);
    setMaxAge(minVal);
    return;
  }

  // Both set → ensure min ≤ max
  if (Number(minVal) > Number(maxVal)) {
    setMinAge(maxVal);
    setMaxAge(minVal);
    return;
  }

  setMinAge(minVal);
  setMaxAge(maxVal);
}}
placeholder="100"

                />
              </div>
            </div>

            <div style={{ marginTop: 4 }}>
              <div style={ui.fieldLabel}>Affinity group (optional)</div>
              <input
                disabled={reviewLocked}
                style={{ ...ui.input, ...lockedFieldStyle }}
                value={affinityText}
                onChange={(e) => {
                  if (reviewLocked) return;
                  setAffinityText(e.target.value);
                }}
                placeholder="New to the city"
              />
            </div>
          </div>

          {/* Purpose TAG */}
          <div style={{ marginTop: 16 }}>
<div style={ui.fieldLabel}>
  Purpose <RequiredIndicator complete={!!purposeTag} />
</div>

            <div style={ui.chipWrap}>
              {PURPOSE_OPTIONS.map((p) => {
                const active = purposeTag === p.id;

                return (
            <button
  key={p.id}
  type="button"
  disabled={reviewLocked}
  onClick={() => {
    if (reviewLocked) return;
    setPurposeTag(p.id);
  }}
  style={{
    ...(active ? ui.chipActive : ui.chip),
    ...(reviewLocked ? { ...lockedPillStyle } : {}),

    // ✅ FORCE TEXT COLOR ON THE BUTTON
    color: active ? '#0b0b0b' : '#0b0b0b',
    textDecoration: 'none',

    // ✅ stop iOS default button styling from overriding your palette
    appearance: 'none',
    WebkitAppearance: 'none',
  }}
>
  {p.label}
</button>

                );
              })}
            </div>
          </div>

          {/* Gender split (optional) */}
          <div style={{ marginTop: 16 }}>
            <div style={ui.fieldLabel}>Gender split (optional)</div>

            <div
              style={{
                opacity:
                  genderGate === 'all' &&
                  sexualOrientation !== 'gay_men' &&
                  sexualOrientation !== 'gay_women' &&
                  Number(size || 0) > 0
                    ? 1
                    : 0.45,
                pointerEvents:
                  genderGate === 'all' &&
                  sexualOrientation !== 'gay_men' &&
                  sexualOrientation !== 'gay_women' &&
                  Number(size || 0) > 0
                    ? 'auto'
                    : 'none',
              }}
            >
              <div style={ui.inlineRadios}>
                <label
                  style={{
                    ...ui.radioPill,
                    ...(reviewLocked ? { ...lockedPillStyle } : {}),
                  }}
                >
                  <input
                    type="radio"
                    name="genderSplit"
                    checked={genderSplit === 'yes'}
                    disabled={reviewLocked}
                    onChange={() => {
                      if (reviewLocked) return;

                      setGenderSplit('yes');

                      // default 50 / 50 on enable
                      const cap = Number(size || 0);
                      if (cap > 0) {
                        const men = Math.floor(cap / 2);
                        const women = cap - men;
                        setMenCap(String(men));
                        setWomenCap(String(women));
                      }
                    }}
                  />
                  <span>Yes</span>
                </label>

                <label
                  style={{
                    ...ui.radioPill,
                    ...(reviewLocked ? { ...lockedPillStyle } : {}),
                  }}
                >
                  <input
                    type="radio"
                    name="genderSplit"
                    checked={genderSplit === 'no'}
                    disabled={reviewLocked}
                    onChange={() => {
                      if (reviewLocked) return;

                      setGenderSplit('no');
                      setMenCap('');
                      setWomenCap('');
                    }}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            {!(
              genderGate === 'all' &&
              sexualOrientation !== 'gay_men' &&
              sexualOrientation !== 'gay_women' &&
              Number(size || 0) > 0
            ) && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                {Number(size || 0) <= 0
                  ? 'Set a max event size to enable gender split.'
                  : genderGate !== 'all'
                  ? 'Gender split is unavailable when the event is restricted to one gender.'
                  : 'Gender split is unavailable for single-gender sexual orientations.'}
              </div>
            )}
          </div>

          {/* Explanation when gender split is NO and allowed */}
          {genderSplit === 'no' &&
            genderGate === 'all' &&
            sexualOrientation !== 'gay_men' &&
            sexualOrientation !== 'gay_women' &&
            Number(size || 0) > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                With no gender split, tickets are not reserved by gender and will fill
                on a first-come basis.
              </div>
            )}

          {/* Gender split lane caps (only when Yes is selected) */}
          {genderSplit === 'yes' && (
            <div style={{ marginTop: 10 }}>
              <div style={ui.grid2}>
                <div>
                  <div style={ui.fieldLabel}>Men</div>
                  <input
                    type="number"
                    disabled={reviewLocked}
                    style={{ ...ui.input, ...lockedFieldStyle }}
                    value={menCapVal}
                    onChange={(e) => {
                      if (reviewLocked) return;
                      setMenCap(e.target.value);
                    }}
                    onBlur={() => {
                      if (reviewLocked) return;

                      const cap = Number(size || 0);

                      let men = Number(menCapVal);
                      if (Number.isNaN(men)) men = 0;
                      men = Math.max(0, Math.min(cap, men));
                      setMenCap(String(men));

                      const women = cap - men;
                      setWomenCap(String(Math.max(0, women)));
                    }}
                  />
                </div>

                <div>
                  <div style={ui.fieldLabel}>Women</div>
                  <input
                    type="number"
                    disabled={reviewLocked}
                    style={{ ...ui.input, ...lockedFieldStyle }}
                    value={womenCapVal}
                    onChange={(e) => {
                      if (reviewLocked) return;
                      setWomenCap(e.target.value);
                    }}
                    onBlur={() => {
                      if (reviewLocked) return;

                      const cap = Number(size || 0);

                      let women = Number(womenCapVal);
                      if (Number.isNaN(women)) women = 0;
                      women = Math.max(0, Math.min(cap, women));
                      setWomenCap(String(women));

                      const men = cap - women;
                      setMenCap(String(Math.max(0, men)));
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                Total: <b>{Number(menCapVal || 0) + Number(womenCapVal || 0)}</b> /{' '}
                {size || '—'}
                {!laneCapsValid && (
                  <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 700 }}>
                    (Must equal Size)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Event Tag chips */}
          <div style={{ marginTop: 16 }}>
<div style={ui.fieldLabel}>
  Event tags <RequiredIndicator complete={eventTags.length > 0} />
</div>


            <div
              style={{
                display: 'flex',
                gap: 10,
                overflowX: 'auto',
                paddingBottom: 6,
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE / Edge
              }}
              className="hide-scrollbar"
            >
              {TAG_OPTIONS.map((t) => {
                const active = eventTags.includes(t.id);

                return (
               <button
  key={t.id}
  type="button"
  disabled={reviewLocked}
  onClick={() => {
    if (reviewLocked) return;
    toggleTag(t.id);
  }}
  style={{
    ...(active ? ui.chipActive : ui.chip),
    ...(reviewLocked ? { ...lockedPillStyle } : {}),
    whiteSpace: 'nowrap',
    flexShrink: 0,

    // ✅ FORCE TEXT COLOR ON THE BUTTON
    color: active ? '#0b0b0b' : '#0b0b0b',
    textDecoration: 'none',

    // ✅ stop iOS default button styling
    appearance: 'none',
    WebkitAppearance: 'none',
  }}
>
  {t.label}
</button>

                );
              })}
            </div>

            <div style={ui.miniMeta}>
              Selected tags: {selectedTagLabels.length ? selectedTagLabels.join(', ') : 'None'}
            </div>
          </div>

          {/* Venue Type + Dress Code */}
          <div style={{ marginTop: 16 }}>
            <div style={ui.fieldLabel}>Venue type</div>
            <CustomSelect
              ui={ui}
              value={venueType}
              onChange={(v) => {
                if (reviewLocked) return;
                setVenueType(v);
              }}
              placeholder="Select venue type"
              options={VENUE_TYPES.map((v) => ({ value: v, label: v }))}
            />

            <div style={ui.fieldLabel}>Dress code</div>
            <CustomSelect
              ui={ui}
              value={dressCode}
              onChange={(v) => {
                if (reviewLocked) return;
                setDressCode(v);
              }}
              placeholder="Select dress code"
              options={DRESS_CODES.map((d) => ({ value: d, label: d }))}
            />
          </div>

          {/* Twist (above description) */}
          <div style={{ marginTop: 16 }}>
            <div style={ui.fieldLabel}>Twist</div>
            <input
              disabled={reviewLocked}
              style={{ ...ui.input, ...lockedFieldStyle }}
              value={twist}
              onChange={(e) => {
                if (reviewLocked) return;
                setTwist(e.target.value);
              }}
              placeholder="Never pour your own drink "
            />
          </div>

          {/* Description (single field, not split) */}
          <div style={{ marginTop: 16 }}>
<div style={ui.fieldLabel}>
  Description <RequiredIndicator complete={!!description} />
</div>

            <textarea
              disabled={reviewLocked}
              style={{
                ...ui.textareaLarge,
                ...lockedFieldStyle,
              }}
              value={description}
              onChange={(e) => {
                if (reviewLocked) return;
                setDescription(e.target.value);
              }}
              placeholder="Write a short description of the event"
            />
          </div>
        </div>
      </div>
    </>
  );
}
